"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SearchFilterSummary } from "@/components/search-filter-summary";
import useDataStore from "@/stores/data-store";
import { useTranslations } from "next-intl";
import React, { FormEvent, ReactNode, Suspense } from "react";

interface SearchAccordionProps {
  children: ReactNode;
  triggerContent?: ReactNode;
  topContent?: ReactNode;
  contentClassName?: string;
}

export default function SearchAccordion({
  children,
  triggerContent,
  topContent,
  contentClassName = "search-panel px-4 pb-4 pt-3",
}: SearchAccordionProps) {
  const defaultValue = "search";
  const t = useTranslations();
  const { isSearchAccordionCollapsed, toggleIsSearchAccordionCollapsed } =
    useDataStore();
  const searchAccordionValue = isSearchAccordionCollapsed ? "" : defaultValue;

  // The panel is a real form so Enter in any field triggers implicit
  // submission, which activates the panel's single search button.
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  return (
    <Accordion
      type="single"
      collapsible
      value={searchAccordionValue}
      className="mt-4 overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-surface dark:border-slate-700/80 dark:bg-slate-800 dark:shadow-none"
    >
      <AccordionItem value={defaultValue} className="border-b-0">
        <AccordionTrigger
          className="flex items-center justify-between border-b border-slate-200/80 px-4 py-3.5 font-medium text-slate-900 hover:bg-slate-50/70 hover:no-underline dark:border-slate-700/80 dark:text-slate-100 dark:hover:bg-slate-700/50"
          onClick={() => toggleIsSearchAccordionCollapsed()}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1.5 pr-4">
            <span className="shrink-0 text-[15px] font-semibold">
              {t("action.search")}
            </span>
            {triggerContent}
            {isSearchAccordionCollapsed && (
              <Suspense fallback={null}>
                <SearchFilterSummary />
              </Suspense>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="p-0">
          <form onSubmit={handleSubmit} className={contentClassName}>
            {topContent}
            {children}
          </form>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
